import { useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { Popover } from './Popover'
import { cx } from '@/lib/cx'

export interface MenuItem {
  label: string
  onSelect: () => void
  danger?: boolean
}

/** The ⋮ on a table row. A real menu, so it is reachable by keyboard. */
export function RowMenu({ items, label }: { items: MenuItem[]; label: string }) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative inline-flex" ref={wrap}>
      {/* 44px, not 32: this sits inches from a delete action inside a table
          row, and deleting the wrong prescription is a data-integrity bug. */}
      <button
        type="button"
        ref={trigger}
        className="grid size-touch place-items-center rounded-md text-fg-subtle transition-colors duration-150 ease-standard hover:bg-hover hover:text-fg aria-expanded:bg-hover aria-expanded:text-fg"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="more" size="1.125rem" />
      </button>
      <Popover
        anchor={trigger}
        open={open}
        align="end"
        role="menu"
        className="flex min-w-44 flex-col rounded-md border border-line bg-raised p-1 shadow-lg"
      >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={cx(
                'flex min-h-touch items-center gap-2 rounded-sm px-3 text-left text-sm whitespace-nowrap',
                item.danger ? 'text-danger hover:bg-danger-soft' : 'text-fg hover:bg-hover',
              )}
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
        ))}
      </Popover>
    </div>
  )
}
